/*
 * fm-discount-facet.js
 * ---------------------------------------------------------------------------
 * Client-side "Highest Discount" sort + "% Off" filter for collection & search.
 *
 * WHY CLIENT-SIDE: Shopify's storefront APIs cannot sort by (or facet on) a
 * computed discount %. Few Moda's PUBLIC (non-member) discount is derived in
 * Prefer product.metafields.custom.discount_percent (Ablestar-synced); Liquid
 * falls back to compute (see snippets/fm-discount-value.liquid). Stamped on each
 * grid item as `data-fm-discount`. Non-member only — member ~60% off is useless
 * as a filter.
 *
 * BECAUSE the grid is server-paginated, a correct discount sort/filter needs
 * every product. When (and only when) a discount sort or filter is engaged we
 * lazily fetch the remaining pages, append them to the grid, then filter/sort.
 * Normal browsing is untouched.
 *
 * State lives in the URL:
 *   - fm_discount : repeated/csv keys, e.g. ?fm_discount=40&fm_discount=60
 *   - fm_sort     : "discount-descending" when the Highest Discount sort is on
 * These params ride along inside the existing filter <form>, so they survive
 * the theme's AJAX facet refresh.
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  if (window.__fmDiscountFacetLoaded) return;
  window.__fmDiscountFacetLoaded = true;

  var SORT_VALUE = 'discount-descending';
  var MAX_PAGES = 60; // safety cap for the lazy full-collection load
  var FETCH_CONCURRENCY = 5;

  // Discrete public discount tiers (theme-only; keep in sync with filter-block).
  var BUCKETS = {
    '30': { label: '30% Off', test: function (d) { return d === 30; } },
    '40': { label: '40% Off', test: function (d) { return d === 40; } },
    '50': { label: '50% Off', test: function (d) { return d === 50; } },
    '60': { label: '60% Off', test: function (d) { return d === 60; } }
  };

  var pageCache = {};   // baseUrl -> array of detached <li> (pages beyond page 1)
  var observer = null;
  var applying = false;
  var reapplyQueued = false;

  /* ------------------------------ helpers ------------------------------ */

  function getGrid() {
    return document.querySelector('.product-grid');
  }

  function getParams() {
    return new URLSearchParams(window.location.search);
  }

  function getBuckets() {
    var params = getParams();
    var raw = params.getAll('fm_discount');
    var out = [];
    raw.forEach(function (v) {
      v.split(',').forEach(function (part) {
        part = part.trim();
        if (part && BUCKETS[part] && out.indexOf(part) === -1) out.push(part);
      });
    });
    return out;
  }

  function isSortActive() {
    return getParams().get('fm_sort') === SORT_VALUE;
  }

  function isDiscountMode() {
    return isSortActive() || getBuckets().length > 0;
  }

  function discountOf(li) {
    var v = parseInt(li.getAttribute('data-fm-discount'), 10);
    return isNaN(v) ? 0 : v;
  }

  function matchesBuckets(d, buckets) {
    if (!buckets.length) return true;
    for (var i = 0; i < buckets.length; i++) {
      var b = BUCKETS[buckets[i]];
      if (b && b.test(d)) return true;
    }
    return false;
  }

  function setUrlParam(name, values) {
    var params = getParams();
    params.delete(name);
    (values || []).forEach(function (v) { params.append(name, v); });
    var qs = params.toString();
    var url = window.location.pathname + (qs ? '?' + qs : '') + window.location.hash;
    window.history.replaceState(window.history.state, '', url);
  }

  /* --------------------------- UI syncing --------------------------- */

  function syncUI() {
    var buckets = getBuckets();
    // "% Off" checkboxes reflect the URL (source of truth).
    document.querySelectorAll('input[name="fm_discount"]').forEach(function (cb) {
      cb.checked = buckets.indexOf(cb.value) !== -1;
    });
    // Hidden input keeps fm_sort inside the filter form so it survives AJAX.
    var sortOn = isSortActive();
    document.querySelectorAll('#fm-sort-input').forEach(function (input) {
      input.value = sortOn ? SORT_VALUE : '';
    });
    // Highlight the Highest Discount sort option.
    document.querySelectorAll('.fm-discount-sort-option').forEach(function (a) {
      a.classList.toggle('is-active', sortOn);
    });
  }

  /* --------------------------- lazy full load --------------------------- */

  function baseUrlKey() {
    var params = getParams();
    ['page', 'sections', 'fm_discount', 'fm_sort'].forEach(function (k) { params.delete(k); });
    var qs = params.toString();
    return window.location.pathname + (qs ? '?' + qs : '');
  }

  function fetchPageItems(baseUrl, page) {
    var sep = baseUrl.indexOf('?') === -1 ? '?' : '&';
    return fetch(baseUrl + sep + 'page=' + page, { headers: { 'X-Requested-With': 'fetch' } })
      .then(function (r) { return r.text(); })
      .then(function (text) {
        var doc = new DOMParser().parseFromString(text, 'text/html');
        var grid = doc.querySelector('.product-grid');
        if (!grid) return [];
        return Array.prototype.slice.call(
          grid.querySelectorAll('li.grid__item[data-fm-discount]')
        );
      })
      .catch(function () { return []; });
  }

  function loadRemainingPages(baseUrl) {
    if (pageCache[baseUrl]) return Promise.resolve(pageCache[baseUrl]);

    var collected = [];
    // Seed "seen" with the handles already on page 1 so we can detect Shopify
    // clamping an out-of-range page back to an earlier page (all-duplicate = stop).
    var seen = {};
    var grid = getGrid();
    if (grid) {
      grid.querySelectorAll('li.grid__item[data-fm-handle]').forEach(function (li) {
        seen[li.getAttribute('data-fm-handle')] = true;
      });
    }

    function runBatch(start) {
      if (start > MAX_PAGES) return Promise.resolve();
      var batch = [];
      for (var p = start; p < start + FETCH_CONCURRENCY && p <= MAX_PAGES; p++) batch.push(p);
      return Promise.all(batch.map(function (p) { return fetchPageItems(baseUrl, p); }))
        .then(function (results) {
          var stop = false;
          results.forEach(function (items) {
            if (!items.length) { stop = true; return; }
            var added = 0;
            items.forEach(function (li) {
              var h = li.getAttribute('data-fm-handle');
              if (h && seen[h]) return;
              if (h) seen[h] = true;
              collected.push(li);
              added++;
            });
            // A full page of only duplicates means we've reached the end.
            if (added === 0) stop = true;
          });
          if (stop) return;
          return runBatch(start + FETCH_CONCURRENCY);
        });
    }

    return runBatch(2).then(function () {
      pageCache[baseUrl] = collected;
      return collected;
    });
  }

  function ensureAllLoaded() {
    var grid = getGrid();
    if (!grid) return Promise.resolve();
    var baseUrl = baseUrlKey();
    return loadRemainingPages(baseUrl).then(function (items) {
      var present = {};
      grid.querySelectorAll('li.grid__item[data-fm-handle]').forEach(function (li) {
        present[li.getAttribute('data-fm-handle')] = true;
      });
      items.forEach(function (li) {
        var handle = li.getAttribute('data-fm-handle');
        if (handle && present[handle]) return;
        if (handle) present[handle] = true;
        grid.appendChild(document.importNode(li, true));
      });
    });
  }

  /* --------------------------- filter + sort --------------------------- */

  function filterAndSort() {
    var grid = getGrid();
    if (!grid) return 0;
    var buckets = getBuckets();
    var sortOn = isSortActive();

    var children = Array.prototype.slice.call(grid.children);
    var products = [];
    children.forEach(function (li) {
      if (li.matches && li.matches('li.grid__item[data-fm-discount]')) {
        products.push(li);
      } else if (li.classList && li.classList.contains('grid__item')) {
        // Non-product tiles (review promos, filler dupes): hide in discount mode.
        li.style.display = 'none';
        li.setAttribute('data-fm-hidden', '1');
      }
    });

    var visible = 0;
    products.forEach(function (li) {
      var d = discountOf(li);
      if (matchesBuckets(d, buckets)) {
        li.style.display = '';
        li.removeAttribute('data-fm-hidden');
        visible++;
      } else {
        li.style.display = 'none';
        li.setAttribute('data-fm-hidden', '1');
      }
    });

    if (sortOn) {
      products.sort(function (a, b) { return discountOf(b) - discountOf(a); });
      products.forEach(function (li) { grid.appendChild(li); });
    }

    return visible;
  }

  function updateCount(n) {
    document.querySelectorAll('#ViewResultsBtn').forEach(function (btn) {
      btn.textContent = 'View products (' + n + ')';
    });
  }

  /* --------------------------- active chips --------------------------- */

  function injectChips() {
    var buckets = getBuckets();
    document.querySelectorAll('.fm-active-facet').forEach(function (el) { el.remove(); });
    if (!buckets.length) return;

    document.querySelectorAll('.js-active-facets-container').forEach(function (container) {
      buckets.forEach(function (key) {
        var chip = document.createElement('a');
        chip.href = '#';
        chip.className = 'active-facet-tag fm-active-facet';
        chip.setAttribute('data-fm-bucket', key);
        chip.innerHTML = BUCKETS[key].label + ' <span>&times;</span>';
        container.appendChild(chip);
      });
    });
  }

  /* --------------------------- pagination toggle --------------------------- */

  function togglePagination(hide) {
    document.querySelectorAll('.pagination-wrapper').forEach(function (el) {
      el.style.display = hide ? 'none' : '';
    });
  }

  /* --------------------------- reset (non-discount) --------------------------- */

  function resetView() {
    document.querySelectorAll('[data-fm-hidden]').forEach(function (li) {
      li.style.display = '';
      li.removeAttribute('data-fm-hidden');
    });
    document.querySelectorAll('.fm-active-facet').forEach(function (el) { el.remove(); });
    togglePagination(false);
  }

  /* --------------------------- apply orchestration --------------------------- */

  function setGridBusy(busy) {
    var grid = getGrid();
    if (grid) grid.style.opacity = busy ? '0.4' : '';
  }

  function apply() {
    if (applying) { reapplyQueued = true; return; }
    applying = true;

    syncUI();

    if (!isDiscountMode()) {
      resetView();
      applying = false;
      if (reapplyQueued) { reapplyQueued = false; apply(); }
      return;
    }

    if (observer) observer.disconnect();
    setGridBusy(true);

    ensureAllLoaded()
      .then(function () {
        var n = filterAndSort();
        injectChips();
        togglePagination(true);
        updateCount(n);
      })
      .catch(function (e) { if (window.console) console.warn('[fm-discount-facet]', e); })
      .then(function () {
        setGridBusy(false);
        requestAnimationFrame(function () {
          observeGrid();
          applying = false;
          if (reapplyQueued) { reapplyQueued = false; apply(); }
        });
      });
  }

  var applyTimer = null;
  function scheduleApply() {
    clearTimeout(applyTimer);
    applyTimer = setTimeout(apply, 60);
  }

  /* --------------------------- observers + events --------------------------- */

  function observeGrid() {
    var grid = getGrid();
    if (!grid) return;
    if (!observer) {
      observer = new MutationObserver(function () {
        if (applying) return;
        scheduleApply();
      });
    }
    observer.observe(grid, { childList: true });
  }

  function closeSortDropdowns() {
    document.querySelectorAll('.custom_sort_container.is-open').forEach(function (c) {
      c.classList.remove('is-open');
    });
  }

  function readCheckedBucketsFromDom() {
    var out = [];
    document.querySelectorAll('input[name="fm_discount"]:checked').forEach(function (cb) {
      if (BUCKETS[cb.value] && out.indexOf(cb.value) === -1) out.push(cb.value);
    });
    return out;
  }

  function onDiscountCheckboxChange(e) {
    if (!e.target || e.target.name !== 'fm_discount') return;
    e.stopPropagation();
    setUrlParam('fm_discount', readCheckedBucketsFromDom());
    apply();
  }

  function onClickCapture(e) {
    // Our "Highest Discount" sort option.
    var discSort = e.target.closest ? e.target.closest('.fm-discount-sort-option') : null;
    if (discSort) {
      e.preventDefault();
      e.stopPropagation();
      setUrlParam('fm_sort', [SORT_VALUE]);
      closeSortDropdowns();
      apply();
      return;
    }

    // A real (Shopify) sort option turns our discount sort off. Clear both the
    // URL param and the hidden input BEFORE the theme's own handler runs, so the
    // theme's AJAX/navigation doesn't carry fm_sort forward.
    var realSort = e.target.closest ? e.target.closest('.sort_link, .sort-option') : null;
    if (realSort && isSortActive()) {
      setUrlParam('fm_sort', []);
      document.querySelectorAll('#fm-sort-input').forEach(function (input) { input.value = ''; });
    }

    // Removing a discount chip.
    var chip = e.target.closest ? e.target.closest('.fm-active-facet') : null;
    if (chip) {
      e.preventDefault();
      e.stopPropagation();
      var key = chip.getAttribute('data-fm-bucket');
      var buckets = getBuckets().filter(function (b) { return b !== key; });
      setUrlParam('fm_discount', buckets);
      document.querySelectorAll('input[name="fm_discount"][value="' + key + '"]').forEach(function (cb) {
        cb.checked = false;
      });
      apply();
    }
  }

  function init() {
    // Only run where a discount UI is present (collection / search results).
    if (!document.querySelector('.fm-discount-filter') || !getGrid()) return;

    document.addEventListener('click', onClickCapture, true);
    // Capture so we run even if other form handlers stop bubbling.
    document.addEventListener('change', onDiscountCheckboxChange, true);
    window.addEventListener('popstate', scheduleApply);
    // After theme.liquid replaces collection section HTML (real Shopify filters).
    document.addEventListener('fm:collection-dom-updated', scheduleApply);

    observeGrid();
    apply();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
